import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Card, CardContent, Typography, Stack, Button, Table, TableHead, TableBody, TableRow, TableCell,
    IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, LinearProgress,
    MenuItem, FormControlLabel, Switch
} from '@mui/material';
import { Add, Edit, Delete, CheckCircle, Cancel } from '@mui/icons-material';
import Navbar from './Navbar';
import {
    fetchPromoCodes,
    createPromoCode,
    updatePromoCode,
    updatePromoCodeStatus,
    deletePromoCode,
    reset
} from '../../features/setup/promoCodesSlice';

const initialFormState = {
    code: '',
    discountType: 'percent',
    discountValue: '',
    maxDiscount: '',
    minOrderAmount: '',
    redemptionLimit: '',
    branchID: '',
    validFrom: '',
    validTo: '',
    isActive: true
};

const toDateTimeLocal = (value) => {
    if (!value) {
        return '';
    }

    const dateObj = new Date(value);
    if (Number.isNaN(dateObj.getTime())) {
        return '';
    }

    const pad = (n) => String(n).padStart(2, '0');
    return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}T${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
};

export default function PromoCodesManagement() {
    const dispatch = useDispatch();
    const { promoCodes, isLoading, isError, isSuccess, message } = useSelector(state => state.promoCodes);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        dispatch(fetchPromoCodes());
    }, [dispatch]);

    useEffect(() => {
        if (isSuccess && message) {
            alert(message);
            dispatch(reset());
            setDialogOpen(false);
            setEditId(null);
            setFormData(initialFormState);
            dispatch(fetchPromoCodes());
        }

        if (isError && message) {
            alert(message);
            dispatch(reset());
        }
    }, [isSuccess, isError, message, dispatch]);

    const openDialog = (promo) => {
        if (!promo) {
            setEditId(null);
            setFormData(initialFormState);
            setDialogOpen(true);
            return;
        }

        setEditId(promo.promoID);
        setFormData({
            code: promo.code || '',
            discountType: promo.discountType || 'percent',
            discountValue: promo.discountValue ?? '',
            maxDiscount: promo.maxDiscount ?? '',
            minOrderAmount: promo.minOrderAmount ?? '',
            redemptionLimit: promo.redemptionLimit ?? '',
            branchID: promo.branchID ?? '',
            validFrom: toDateTimeLocal(promo.validFrom),
            validTo: toDateTimeLocal(promo.validTo),
            isActive: Number(promo.isActive) === 1
        });
        setDialogOpen(true);
    };

    const payload = useMemo(() => ({
        code: String(formData.code || '').trim().toUpperCase(),
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        maxDiscount: formData.maxDiscount === '' ? null : Number(formData.maxDiscount),
        minOrderAmount: formData.minOrderAmount === '' ? 0 : Number(formData.minOrderAmount),
        redemptionLimit: formData.redemptionLimit === '' ? null : Number(formData.redemptionLimit),
        branchID: formData.branchID === '' ? null : Number(formData.branchID),
        validFrom: formData.validFrom || null,
        validTo: formData.validTo || null,
        isActive: formData.isActive ? 1 : 0
    }), [formData]);

    const handleSave = () => {
        if (!payload.code) {
            alert('Promo code is required');
            return;
        }
        if (!payload.discountValue || payload.discountValue <= 0) {
            alert('Discount value must be greater than 0');
            return;
        }
        if (payload.minOrderAmount < 0) {
            alert('Minimum order amount cannot be negative');
            return;
        }
        if (payload.redemptionLimit !== null && payload.redemptionLimit <= 0) {
            alert('Redemption limit must be greater than 0');
            return;
        }

        if (editId) {
            dispatch(updatePromoCode({ promoID: editId, data: payload }));
        } else {
            dispatch(createPromoCode(payload));
        }
    };

    const handleStatusToggle = (promo) => {
        dispatch(updatePromoCodeStatus({
            promoID: promo.promoID,
            isActive: Number(promo.isActive) === 1 ? 0 : 1
        }));
    };

    const handleDelete = (promoID) => {
        if (window.confirm('Delete this promo code?')) {
            dispatch(deletePromoCode(promoID));
        }
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                {isLoading && <LinearProgress />}
                <Card>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h5" fontWeight="bold">Promo Codes Management</Typography>
                            <Button startIcon={<Add />} variant="contained" onClick={() => openDialog(null)}>
                                Add Promo Code
                            </Button>
                        </Stack>

                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Code</strong></TableCell>
                                    <TableCell><strong>Discount</strong></TableCell>
                                    <TableCell><strong>Min Order</strong></TableCell>
                                    <TableCell><strong>Max Discount</strong></TableCell>
                                    <TableCell><strong>Limit</strong></TableCell>
                                    <TableCell><strong>Validity</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell align="center"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {promoCodes.map((promo) => (
                                    <TableRow key={promo.promoID}>
                                        <TableCell>{promo.code}</TableCell>
                                        <TableCell>
                                            {promo.discountType === 'percent'
                                                ? `${Number(promo.discountValue || 0).toFixed(2)}%`
                                                : `Rs. ${Number(promo.discountValue || 0).toFixed(2)}`}
                                        </TableCell>
                                        <TableCell>Rs. {Number(promo.minOrderAmount || 0).toFixed(2)}</TableCell>
                                        <TableCell>
                                            {promo.maxDiscount !== null && promo.maxDiscount !== undefined
                                                ? `Rs. ${Number(promo.maxDiscount).toFixed(2)}`
                                                : '-'}
                                        </TableCell>
                                        <TableCell>{promo.redemptionLimit ?? 'Unlimited'}</TableCell>
                                        <TableCell>
                                            {promo.validFrom ? new Date(promo.validFrom).toLocaleDateString() : 'Always'}
                                            {' - '}
                                            {promo.validTo ? new Date(promo.validTo).toLocaleDateString() : 'Always'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={Number(promo.isActive) === 1 ? 'Active' : 'Inactive'}
                                                color={Number(promo.isActive) === 1 ? 'success' : 'default'}
                                                size="small"
                                                icon={Number(promo.isActive) === 1 ? <CheckCircle /> : <Cancel />}
                                                onClick={() => handleStatusToggle(promo)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton color="primary" size="small" onClick={() => openDialog(promo)}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton color="error" size="small" onClick={() => handleDelete(promo.promoID)}>
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>{editId ? 'Edit Promo Code' : 'Add Promo Code'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="Promo Code"
                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                required
                            />
                            <TextField
                                select
                                label="Discount Type"
                                value={formData.discountType}
                                onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                            >
                                <MenuItem value="percent">Percentage</MenuItem>
                                <MenuItem value="flat">Flat Amount</MenuItem>
                            </TextField>
                            <TextField
                                label={formData.discountType === 'percent' ? 'Discount (%)' : 'Discount Amount'}
                                type="number"
                                value={formData.discountValue}
                                onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                                required
                            />
                            <TextField
                                label="Max Discount (optional)"
                                type="number"
                                value={formData.maxDiscount}
                                onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
                                disabled={formData.discountType !== 'percent'}
                            />
                            <TextField
                                label="Minimum Order Amount"
                                type="number"
                                value={formData.minOrderAmount}
                                onChange={(e) => setFormData({ ...formData, minOrderAmount: e.target.value })}
                            />
                            <TextField
                                label="Redemption Limit (optional)"
                                type="number"
                                value={formData.redemptionLimit}
                                onChange={(e) => setFormData({ ...formData, redemptionLimit: e.target.value })}
                            />
                            <TextField
                                label="Branch ID (optional)"
                                type="number"
                                value={formData.branchID}
                                onChange={(e) => setFormData({ ...formData, branchID: e.target.value })}
                            />
                            <TextField
                                label="Valid From"
                                type="datetime-local"
                                value={formData.validFrom}
                                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Valid To"
                                type="datetime-local"
                                value={formData.validTo}
                                onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}
